import { z } from 'zod'
import { requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Proyecto inmobiliario — fuente única de verdad para la validación del API
 * (nestjs-zod `createZodDto`) y del formulario web (RHF + zodResolver).
 *
 * `name` y `code` son únicos GLOBALES (a diferencia de los subtipos, que son
 * únicos por proyecto). `requiresAuthorization`/`authorizerIds` forman parte
 * del contrato: el DTO legacy no los declaraba pero el form siempre los envió
 * y reservas/disponibilidad los leen.
 */

export const CURRENCY = {
  USD: 'USD',
  GTQ: 'GTQ'
} as const
export type Currency = (typeof CURRENCY)[keyof typeof CURRENCY]

// ObjectId de Mongo serializado (24 hex): el wire actual identifica la
// sociedad y los autorizadores por ObjectId; no cambiar hasta contrato v2.
const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

const ProjectInputBaseSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  code: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  society: objectIdString,
  currency: z.nativeEnum(CURRENCY, {
    errorMap: () => ({ message: 'Debe seleccionar una moneda' })
  }),
  /** URL de la imagen/logo del proyecto (la consumen los PDFs generados). */
  image: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined),
  /**
   * URL del logo CUADRADO (1024×1024) que la app del cliente pinta sobre la portada.
   *
   * Distinto de `image`, que es el logotipo completo para papelería: a 32 puntos su texto es
   * ilegible. Ver `packages/schemas/src/api-v2/admin-projects.ts`.
   */
  mark: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined),
  requiresAuthorization: z.boolean().optional(),
  authorizerIds: z.array(objectIdString).optional(),
  active: z.boolean().optional()
})

export const CreateProjectInputSchema = ProjectInputBaseSchema.superRefine((value, ctx) => {
  if (value.requiresAuthorization && (value.authorizerIds?.length ?? 0) === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['authorizerIds'],
      message: 'Debe seleccionar al menos un autorizador'
    })
  }
})

// Parcial SIN el refine: un PATCH con `requiresAuthorization: true` y sin
// `authorizerIds` deja la lista existente intacta (semántica de patch).
export const UpdateProjectInputSchema = ProjectInputBaseSchema.partial()

export type CreateProjectInput = z.infer<typeof CreateProjectInputSchema>
export type UpdateProjectInput = z.infer<typeof UpdateProjectInputSchema>
